// ============================================================================
// confirm-dialog-slot.ts
// ----------------------------------------------------------------------------
// The app holds ONE confirm dialog at a time (App's showConfirm). Every confirm
// used to open from a click, so replacing the open one never mattered. The
// Stage 1 import's covering-email question opens from a running import, and
// release review 3 (2026-10-21) found it could replace a dialog the operator
// had open: that dialog's callbacks never ran, a promise-wrapped one never
// settled, and a click meant for "Remove" landed on "Use the attached request".
//
// So, for every caller:
//   - a dialog that is replaced is settled as closed without a choice (its
//     onDismiss runs), so a promise-wrapped caller never hangs;
// and for a caller that asks to wait (`queue: true`):
//   - it opens only when no dialog is open anywhere: this slot's, or another
//     modal on screen (`otherDialogOpen`: Photo Markup, a confirm rendered by
//     its own page). Release review 4 (2026-10-21) found it opening under
//     Photo Markup and over a rail confirm;
//   - it opens QUEUED_DIALOG_DELAY_MS after the LAST dialog closed, timed again
//     on every close, so a second click or Enter meant for that dialog never
//     answers it;
//   - it is withdrawn (onDismiss runs) when its `stillWanted` says no, for
//     example after the import that raised it was cancelled: before it opens,
//     and while it is open (release review 5, S5);
//   - when another dialog on screen goes, the gap is timed from the first look
//     that finds it gone (release review 5, S6 and S11);
//   - `onWaiting` tells the caller it is waiting behind another dialog, and
//     `onShown` when it opens, so nothing waits unseen (release review 5, S7).
// ============================================================================

export interface ConfirmSlotOptions {
    onDismiss?: () => void;
    /** Wait until no dialog is open instead of replacing the open one. */
    queue?: boolean;
    /** Asked while it waits and while it is open: false withdraws it, and its onDismiss runs. */
    stillWanted?: () => boolean;
    /** It is waiting behind another dialog. */
    onWaiting?: () => void;
    /** It has opened. */
    onShown?: () => void;
    [key: string]: unknown;
}

/** How long a waiting dialog stays closed after the last dialog closed. */
export const QUEUED_DIALOG_DELAY_MS = 350;
/** How often a waiting dialog looks again while another dialog is open. */
export const QUEUED_DIALOG_POLL_MS = 250;

export function createConfirmDialogSlot({
    render,
    hide,
    otherDialogOpen = () => false,
    now = () => Date.now(),
    schedule = (run: () => void, ms: number) => { setTimeout(run, ms); },
}: {
    render: (options: ConfirmSlotOptions) => void;
    hide: () => void;
    otherDialogOpen?: () => boolean;
    now?: () => number;
    schedule?: (run: () => void, ms: number) => void;
}) {
    let open = false;
    let dismiss: (() => void) | null = null;
    let lastBusyAt = Number.NEGATIVE_INFINITY;
    let sawBusy = false;
    let timerPending = false;
    let current: ConfirmSlotOptions | null = null;
    const waiting: ConfirmSlotOptions[] = [];

    const wanted = (options: ConfirmSlotOptions) => typeof options.stillWanted !== 'function' || options.stillWanted();

    const present = (options: ConfirmSlotOptions) => {
        const replaced = open ? dismiss : null;
        dismiss = typeof options.onDismiss === 'function' ? options.onDismiss : null;
        open = true;
        current = options;
        render(options);
        options.onShown?.();
        if (replaced) replaced();
        if (typeof options.stillWanted === 'function') watchOpen(options);
    };

    // An open dialog whose caller no longer wants it closes itself.
    const watchOpen = (options: ConfirmSlotOptions) => {
        schedule(() => {
            if (!open || current !== options) return;
            if (!wanted(options)) {
                close();
                return;
            }
            watchOpen(options);
        }, QUEUED_DIALOG_POLL_MS);
    };

    const wake = (ms: number) => {
        if (timerPending) return;
        timerPending = true;
        schedule(() => {
            timerPending = false;
            pump();
        }, ms);
    };

    const pump = () => {
        while (waiting.length > 0 && !wanted(waiting[0]!)) waiting.shift()!.onDismiss?.();
        if (waiting.length === 0) return;
        if (open || otherDialogOpen()) {
            lastBusyAt = now();
            sawBusy = true;
            wake(QUEUED_DIALOG_POLL_MS);
            return;
        }
        // The first look that finds the other dialog gone: it closed some time
        // since the last look, so the full gap runs from now.
        if (sawBusy) {
            sawBusy = false;
            lastBusyAt = now();
        }
        const wait = QUEUED_DIALOG_DELAY_MS - (now() - lastBusyAt);
        if (wait > 0) {
            wake(wait);
            return;
        }
        present(waiting.shift()!);
    };

    const show = (options: ConfirmSlotOptions) => {
        if (!options.queue) {
            present(options);
            return;
        }
        waiting.push(options);
        pump();
        if (waiting.includes(options)) options.onWaiting?.();
    };

    /** `dropWaiting` (sign-out) settles every waiting dialog as closed without a choice. */
    const close = ({ dropWaiting = false }: { dropWaiting?: boolean } = {}) => {
        hide();
        open = false;
        current = null;
        lastBusyAt = now();
        sawBusy = false;
        const closed = dismiss;
        dismiss = null;
        if (closed) closed();
        if (dropWaiting) {
            for (const options of waiting.splice(0)) options.onDismiss?.();
            return;
        }
        if (waiting.length > 0) wake(QUEUED_DIALOG_DELAY_MS);
    };

    return { show, close, isOpen: () => open, waitingCount: () => waiting.length };
}
